import axios from 'axios';
import classNames from 'classnames/bind';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { validateEmail, validateLength } from 'Utils/Validators';
import { stripTags } from 'Utils';
import { gaPush } from 'Utils/ga_push';

import { Congratulations } from 'Containers/ContactForm/LeadForm/Congratulations';
import { gReCaptchaWebsiteKey } from 'Containers/ContactForm/GoogleReCaptchaUtils';

import Button from 'Components/Button';
import Dropdown from 'Components/Dropdown';
import Input from 'Components/Input';
import Radio from 'Components/Radio';
import Textarea from 'Components/Textarea';
import InputDate, { WEEKDAYS_LONG } from 'Components/Input/Date';

import { disabledDays } from './disabledDays';
import { CarImage } from './CarImage';
import { formTypes } from './formTypes';
import { Footer } from './Footer';
import { Header } from './Header';

import styles from './styles.scss';
import { FormErrorModal } from 'Containers/ContactForm/FormErrorModal';
import { AdvertisementCommunicationInfo } from 'Containers/ContactForm/AdvertisementCommunicationInfo';

const category = 'Leads';
const footnote =
  'Weitere Informationen zur elektrischen Reichweite, Energiekosten, KFZ-Steuer und CO<sub>2</sub>-Kosten finden Sie unter <a href="https://www.mazda.de/Energieverbrauch" target="_blank" rel="noopener noreferrer" aria-label="www.mazda.de/Energieverbrauch - öffnet in einem neuen Fenster oder Tab">www.mazda.de/Energieverbrauch</a>. Mit Klick auf den Link gelangen Sie auf die Informationsseite der Mazda Motors (Deutschland) GmbH.';
const recaptchaSrc = `https://www.recaptcha.net/recaptcha/api.js?render=${gReCaptchaWebsiteKey}`;
const animationDuration = 500;
const preferredPaymentOptions = [
  { value: 'unspecified', label: 'Keine Angabe' },
  { value: 'cash', label: 'Bar' },
  { value: 'finance', label: 'Finanzierung' },
  { value: 'leasing', label: 'Leasing' },
];
const cx = classNames.bind(styles);

const validClientTypes = ['private', 'business'];
const capitalizeFirstLetter = (val) => String(val).charAt(0).toUpperCase() + String(val).slice(1);

const findInObject = (haystack, needle) =>
  haystack.find((cl) => cl.deeplink === needle) ?? haystack.find((cl) => cl.moi === needle);

export function LeadForm({
  dealer,
  defaultModel,
  modalType,
  onClose,
  sharedState,
  updateMultipleUrlQuery,
  updateOneUrlQuery,
  setCloseTracking,
  urlParams,
  storybook,
}) {
  const contentRef = useRef(null);

  const [init, setInit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasShadow, setHasShadow] = useState(false);
  const [activeStep, setActiveStep] = useState(undefined);
  const [keepStep, setKeepStep] = useState(undefined);
  const [model, setModel] = useState(defaultModel || sharedState.carModel || '');
  const [modelBefore, setModelBefore] = useState('');
  const [completed, setCompleted] = useState(false);
  const [transferError, setTransferError] = useState(undefined);
  const [trackedError, setTrackedError] = useState('');
  const [trackedStart, setTrackedStart] = useState(false);
  const [errors, setErrors] = useState({
    firstName: 'init',
    lastName: 'init',
    email: 'init',
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [comment, setComment] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(modalType.name === 'NEWSLETTER');
  const [telephoneOptIn, setTelephoneOptIn] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState(undefined);
  const [preferredTime, setPreferredTime] = useState(undefined);

  let defaultClientType = capitalizeFirstLetter(validClientTypes[0]);
  if (urlParams.clientType && validClientTypes.includes(urlParams.clientType.toLowerCase())) {
    defaultClientType = capitalizeFirstLetter(urlParams.clientType);
  } else if (
    sharedState.clientType &&
    validClientTypes.includes(sharedState.clientType.toLowerCase())
  ) {
    defaultClientType = capitalizeFirstLetter(sharedState.clientType);
  }
  const [clientType, setClientType] = useState(defaultClientType);
  const [preferredPayment, setPreferredPayment] = useState('');

  const form = useMemo(() => formTypes.find((t) => t.name === modalType.name), [modalType]);

  const validSteps = useMemo(
    () =>
      form.steps.reduce((acc, step) => {
        let valid;
        switch (step.label) {
          case 'Modellauswahl':
            valid = model !== '';
            break;
          case 'Kontaktdaten':
            valid = Object.keys(errors).length === 0;
            break;
          case 'Wunschtermin':
          case 'Zusatzinfos':
          default:
            valid = true;
        }
        return [...acc, valid];
      }, []),
    [form.steps, activeStep, model, errors, appointmentDate]
  );

  const modelOptions = useMemo(
    () =>
      modalType.name === 'NEWSLETTER'
        ? (window.hwsconfig.carlines || [])
            .filter((t) => t.deeplink === sharedState.carModel)
            .map((x) => ({ ...x, value: x.deeplink }))
        : (window.hwsconfig.carlines || [])
            .reduce(
              (acc, carline) =>
                carline.leadform[form.leadform]
                  ? [
                      ...acc,
                      {
                        ...carline,
                        label: carline.name,
                      },
                    ]
                  : acc,
              []
            )
            .map((x) => ({ ...x, value: x.deeplink })),
    [window.hwsconfig.carlines, model, form]
  );

  const consumptionText = useMemo(
    () => window.hwsconfig[form.consumptionText],
    [window.hwsconfig, form]
  );

  const [
    stepNewsletter,
    step1visible,
    step2visible,
    step3visible,
    step4visible,
    lastStep,
    showLargeCarIn,
    showLargeCarOut,
    showSmallCarIn,
    showSmallCarOut,
  ] = useMemo(() => {
    const ls = form.steps.length - 1;

    return modalType.name === 'NEWSLETTER'
      ? // the newsletter modal has a fixed state
        [true, false, false, false, false, ls, false, false, true, false]
      : [
          false,
          activeStep === 0 || [0, 1].includes(keepStep),
          activeStep === 1 && ![0, 1, 3].includes(keepStep),
          activeStep === 2 && ![2, 3].includes(keepStep),
          activeStep === ls || keepStep === ls,
          ls,
          (activeStep === 0 || keepStep === 1) && (model !== '' || modelBefore !== ''),
          keepStep === 0 && (model !== '' || modelBefore !== ''),
          ([1, 2].includes(activeStep) || keepStep === 3) && (model !== '' || modelBefore !== ''),
          ((activeStep === 3 && keepStep === 2) || keepStep === 1) &&
            (model !== '' || modelBefore !== ''),
        ];
  }, [form.steps, activeStep, keepStep, model, modelBefore]);

  const currentModel = useMemo(
    () => findInObject(window.hwsconfig.carlines, model),
    [window.hwsconfig.carlines, model]
  );

  const [appointmentDateString, appointmentDateStringApi] = useMemo(() => {
    const appFallback = 'Keine Angabe';
    const apiFallback = 'keine Terminauswahl';
    const ret = appointmentDate
      ? `${WEEKDAYS_LONG[appointmentDate.getDay()]}, ${appointmentDate.getDate()}.${appointmentDate.getMonth() + 1}.${appointmentDate.getFullYear()}`
      : appFallback;

    return [ret, ret === appFallback ? apiFallback : ret];
  }, [appointmentDate]);

  const minDate = useMemo(
    () =>
      // the earliest possible date for a test drive should be the day after tomorrow 🥶
      new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 3)
        .toISOString()
        .slice(0, 10),
    []
  );

  const startTracking = useCallback(() => {
    if (init || trackedStart) {
      return;
    }
    setTrackedStart(true);
    gaPush({
      ...{
        event: 'form_start',
        form_name: form.form_name,
        model: currentModel ? currentModel.label : undefined,
        category,
      },
      ...(form.form_name === 'newsletter'
        ? {
            form_step: form.steps[0].form_step,
          }
        : {}),
      ...(form.form_name === 'test_drive_request'
        ? {
            form_step: form.steps[1].form_step,
          }
        : {}),
      ...(form.form_name === 'order_pre_order'
        ? {
            form_step: form.steps[1].form_step,
            form_funding_type: undefined,
            form_contact_type: undefined,
          }
        : {}),
    });
  }, [init, trackedStart, activeStep, currentModel, form, category]);

  const handleModelChange = useCallback(
    (value, property) => {
      if (model) {
        setModelBefore(model);
      }
      setModel(value);
      updateOneUrlQuery(value, property);
      window.setTimeout(resize, 150);
      if (!trackedStart) {
        const cm = findInObject(window.hwsconfig.carlines, value);
        setTrackedStart(true);
        gaPush({
          ...{
            event: 'form_start',
            form_name: form.form_name,
            form_step: form.steps[0].form_step,
            model: cm ? cm.label : undefined,
            category,
          },
          ...(form.form_name === 'order_pre_order'
            ? {
                form_funding_type: undefined,
                form_contact_type: undefined,
              }
            : {}),
        });
      }
    },
    [model, window.hwsconfig.carlines]
  );

  const resize = useCallback(() => {
    if (contentRef.current) {
      setHasShadow(contentRef.current.clientHeight < contentRef.current.scrollHeight);
    }
  }, []);

  const resetKeepStep = useCallback(() => {
    window.setTimeout(() => setKeepStep(undefined), animationDuration);
  }, [setKeepStep]);

  const getKeepStepFromDestination = useCallback((destination) => {
    switch (destination) {
      case 0:
        return activeStep > 0 ? 1 : undefined;
      case 1:
        return activeStep === 0 ? 0 : undefined;
      case 3:
        return activeStep === 2 ? 2 : undefined;
      default:
        return undefined;
    }
  });

  const goBack = useCallback(() => {
    const nextActive = activeStep - 1;
    let nextKeep = lastStep;

    if (activeStep !== lastStep) {
      nextKeep = activeStep === 1 ? 1 : undefined;
    }

    if (nextActive === 0) {
      setModelBefore('');
      setModel('');
    }

    setActiveStep(nextActive);
    setKeepStep(nextKeep);

    if (nextKeep !== undefined) {
      resetKeepStep();
    }

    gaPush({
      event: 'form_interaction',
      form_name: form.form_name,
      form_step: form.steps[activeStep].form_step,
      click_value: `form back_${form.steps[activeStep].form_step}`,
      click_type: 'form back button',
      category,
    });
  }, [activeStep, lastStep, setKeepStep, setActiveStep]);

  const goNext = useCallback(() => {
    gaPush({
      ...{
        event: 'form_progress',
        form_name: form.form_name,
        form_step: form.steps[activeStep].form_step,
        form_progress_step: form.steps[activeStep].form_step,
        model: currentModel.label,
        category,
      },
      ...(form.form_name === 'order_pre_order'
        ? {
            form_funding_type: preferredPayment || (activeStep > 1 ? 'unspecified' : undefined),
            form_contact_type: activeStep > 1 ? clientType : undefined,
          }
        : {}),
    });
    let nextActive = activeStep + 1;
    let nextKeep = activeStep === 0 ? 0 : undefined;

    if (activeStep === lastStep - 1) {
      nextActive = lastStep;
      nextKeep = activeStep === lastStep - 1 ? lastStep - 1 : undefined;
    }

    setActiveStep(nextActive);
    setKeepStep(nextKeep);

    if (nextKeep !== undefined) {
      resetKeepStep();
    }
  }, [
    activeStep,
    lastStep,
    setKeepStep,
    setActiveStep,
    currentModel,
    preferredPayment,
    clientType,
  ]);

  const goToStep = useCallback(
    (next, keep, click_type = 'breadcrumb') => {
      const ks = keep || getKeepStepFromDestination(next);

      if (ks) {
        setKeepStep(ks);
        resetKeepStep();
      }

      if (next === 0) {
        setModelBefore('');
        setModel('');
      }

      setActiveStep(next);

      gaPush({
        event: 'form_interaction',
        form_name: form.form_name,
        form_step: form.steps[activeStep].form_step,
        click_value:
          click_type === 'breadcrumb'
            ? `breadcrumb_${form.steps[next].number}`
            : `change_${form.steps[next].form_step}`,
        click_type,
        category,
      });
    },
    [currentModel, activeStep]
  );

  const transferData = useCallback(() => {
    const payload = {
      type: form.type,
      firstName,
      lastName,
      postalCode: '',
      city: '',
      country: 'Germany',
      dealer: !!dealer.settings.leadsMimicDealerId ? dealer.settings.leadsMimicDealerId : dealer.id,
      telephone,
      email,
      market: 'MMD',
      campaign:
        modalType.name === 'NEWSLETTER'
          ? urlParams.newslettercode || urlParams.newsletterCode || form.campaign
          : sharedState.campaignCode || form.campaign,
      language: 'de',
      assignToDealer: modalType.name !== 'NEWSLETTER',
      organisation: 'MMD Organization',
      clientType,
      customerSupportMessage: '',
      model: currentModel.moi,
      addressLine1: '',
      addressLine2: '',
      comments: form.comments({
        currentModel,
        preferredTime,
        appointmentDateStringApi,
        comment,
        clientType,
        preferredPaymentOptions,
        preferredPayment,
      }),
    };

    if (telephoneOptIn) {
      payload.telephoneOptIn = true;
      payload.smsOptIn = true;
    }

    if (emailOptIn) {
      payload.emailOptIn = true;
    }

    // add engineType to the payload if carline exactly has one
    if ((currentModel.engineTypes || []).length === 1) {
      payload.engineType = currentModel.engineTypes[0];
    }

    // THIS IS ONLY USED BY STORYBOOK
    if (storybook) {
      setCompleted(true);
      return;
    }

    // console.log('send data', modalType.name, payload);
    setLoading(true);

    window.grecaptcha.ready(async () => {
      await window.grecaptcha
        .execute(gReCaptchaWebsiteKey, { action: form.action })
        .then(async (token) => {
          payload.gReCaptchaToken = token;

          await axios
            .post('/api/v1/contact/', payload)
            .then(() => {
              setCompleted(true);
              setLoading(false);
              gaPush({
                ...{
                  event: 'form_end',
                  form_name: form.form_name,
                  form_value: `conversion:${form.form_name}:${currentModel.label}::`,
                  model: currentModel.label,
                  category,
                },
                ...(form.form_name === 'newsletter'
                  ? {
                      form_step: 'form_success_page',
                      form_contact_type: clientType,
                      form_value: 'conversion:newsletter_request:KMI',
                    }
                  : {}),
                ...(form.form_name === 'test_drive_request'
                  ? {
                      form_step: '5_form_success_page',
                      form_funding_type: preferredPayment || 'unspecified',
                      form_contact_type: clientType,
                      form_value: `conversion:${form.form_name}:${currentModel.label}:${clientType}:${preferredPayment || 'unspecified'}`,
                    }
                  : {}),
                ...(form.form_name === 'order_pre_order'
                  ? {
                      form_step: '5_form_success_page',
                      form_funding_type: preferredPayment || 'unspecified',
                      form_contact_type: clientType,
                      form_value: `conversion:${form.form_name}:${currentModel.label}:${clientType}:${preferredPayment || 'unspecified'}`,
                    }
                  : {}),
              });
              setCloseTracking({
                event: 'form_interaction',
                click_type: 'form close button',
                form_name: form.form_name,
                form_step:
                  form.form_name === 'newsletter' ? 'form_success_page' : '5_form_success_page',
                click_value:
                  form.form_name === 'newsletter'
                    ? 'close_form_success_page'
                    : 'close_5_form_success_page',
                category,
              });
            })
            .catch((err) => {
              const tError = err ? JSON.stringify(err.data.error) : 'error response empty';

              setTransferError(tError);

              const eo = err ? err.data.error : {};
              let message = 'error_server';

              try {
                message =
                  eo.status === 403
                    ? 'error_validation' // captcha incorrect
                    : eo.status === 408 || err.code === '-'
                      ? 'error_timeout' // timeout
                      : 'error_server'; // anything else
              } catch (e) {}

              setLoading(false);

              gaPush({
                event: 'form_error',
                form_name: form.form_name,
                form_step:
                  form.form_name === 'newsletter' ? 'form_success_page' : '5_form_success_page',
                error_message: `${form.form_name}:submit:${message}`,
                model: currentModel.label,
                category,
              });
            });
        });
    });
  }, [
    firstName,
    lastName,
    email,
    telephone,
    comment,
    emailOptIn,
    telephoneOptIn,
    appointmentDate,
    preferredTime,
    preferredPayment,
    errors,
    form,
    model,
    dealer,
    sharedState,
    clientType,
    consumptionText,
    currentModel,
  ]);

  /***************************************************/

  // onload
  useEffect(() => {
    if (!document.querySelector(`script[src="${recaptchaSrc}"]`)) {
      const script = document.createElement('script');
      script.id = 'recaptcha-script';
      script.src = recaptchaSrc;
      document.body.appendChild(script);
    }

    if (!window.deactivatedServiceMessage) {
      const url = {
        modaltype: modalType.name,
        showmodal: 'true',
      };

      const mop = findInObject(modelOptions, model);
      if (mop) {
        url.carmodel = mop.deeplink;
      }

      updateMultipleUrlQuery(url);
    }

    window.deactivatedServiceMessage = undefined;
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  // init
  useEffect(() => {
    if (!init || !contentRef.current) {
      return;
    }

    const loadTracking = {
      ...{
        event: 'form_load',
        form_name: form.form_name,
        form_step: form.steps[0].form_step,
        model: undefined,
        category,
      },
      ...(form.form_name === 'order_pre_order'
        ? {
            form_funding_type: undefined,
            form_contact_type: undefined,
          }
        : {}),
    };

    if (!model) {
      setActiveStep(0);
    } else {
      if (modalType.name === 'NEWSLETTER') {
        loadTracking.model = currentModel.label;
        setActiveStep(0);
      } else {
        if (findInObject(modelOptions, model)) {
          setActiveStep(0);
          loadTracking.form_step = form.steps[1].form_step;
          loadTracking.model = currentModel.label;
        } else {
          // model in url is not available in modelOptions
          updateMultipleUrlQuery({ carmodel: '' });
          setActiveStep(0);
        }
      }
    }

    gaPush(loadTracking);

    setInit(false);
    window.setTimeout(resize, 150);
  }, [contentRef.current, modelOptions, init]);

  // step change, model reset, scroll up, close event tracking
  useEffect(() => {
    if (contentRef.current) {
      document.getElementById('lf-top').scrollIntoView();
      contentRef.current.scroll({ top: 0, left: 0 });
    }

    setCloseTracking({
      event: 'form_interaction',
      click_type: 'form close button',
      form_name: form.form_name,
      form_step: form.steps[activeStep || 0].form_step,
      click_value: `close_${form.steps[activeStep || 0].form_step}`,
      category,
    });
    window.setTimeout(resize, animationDuration);
  }, [contentRef.current, activeStep]);

  useEffect(() => {
    contentRef.current.closest('[role="dialog"]')?.focus();

    setTimeout(() => {
      const headline = contentRef.current?.querySelector('h3');
      if (headline) {
        headline.focus();
      }
    }, 500);
  }, [activeStep]);

  useEffect(() => {
    contentRef.current?.closest('[role="dialog"]')?.focus();
  }, [loading]);

  // error tracking
  useEffect(() => {
    if (init || Object.keys(errors).length === 0) {
      return;
    }

    const errorStrings = {
      firstName: 'missing first name',
      lastName: 'missing last name',
      telephone: 'telephone number invalid',
      // 'email': 'invalid email format', // left out because it fires everytime until a valid format is entered
    };

    const error_message = Object.keys(errors).reduce(
      (acc, val) =>
        Object.keys(errorStrings).includes(val) && errors[val] !== 'init'
          ? [...(acc ? acc.split(':') : []), errorStrings[val]].join(':')
          : acc,
      ''
    );

    // only if error and never repeat the last error
    if (error_message && error_message !== trackedError) {
      setTrackedError(error_message);
      gaPush({
        event: 'form_error',
        form_name: form.form_name,
        form_step: form.steps[activeStep].form_step,
        model: currentModel.label,
        error_message: `${form.form_name}:next_step:${error_message}`,
        category,
      });
    }
  }, [errors]);

  useEffect(() => {
    validateLength(errors, 'firstName', firstName, 1, 48, setErrors);
    startTracking();
  }, [firstName]);

  useEffect(() => {
    validateLength(errors, 'lastName', lastName, 1, 48, setErrors);
    startTracking();
  }, [lastName]);

  useEffect(() => {
    if (!email && errors.email === 'init') {
      return;
    }

    validateEmail(errors, 'email', email, setErrors);
    startTracking();
  }, [email]);

  useEffect(() => {
    validateLength(errors, 'telephone', telephone, 0, 25, setErrors);
    startTracking();
  }, [telephone]);

  useEffect(() => {
    validateLength(errors, 'comment', comment, 0, 2000, setErrors);
    startTracking();
  }, [comment]);

  // THIS IS ONLY USED BY STORYBOOK
  useEffect(() => {
    if (storybook) {
      setFirstName(storybook.firstName);
      setLastName(storybook.lastName);
      setEmail(storybook.email);
      setTelephone(storybook.telephone);
      setComment(storybook.comment);
      setEmailOptIn(storybook.emailOptIn);
      setTelephoneOptIn(storybook.telephoneOptIn);
      setAppointmentDate(storybook.appointmentDate);
      setPreferredTime(storybook.preferredTime);
      setActiveStep(storybook.activeStep);
      setCompleted(storybook.completed);
      setTransferError(storybook.transferError);
      setClientType(storybook.clientType);
      setPreferredPayment(storybook.preferredPayment);
    }
  }, [init, storybook]);
  /***************************************************/

  if (transferError) {
    return (
      <div className={cx(styles.containerError)}>
        <FormErrorModal dealer={window.dealer} />
      </div>
    );
  }

  if (!transferError && completed) {
    return (
      <Congratulations
        nameBodyText={currentModel.nameBodyText || currentModel.label}
        modalType={modalType}
        onClose={onClose}
        gaPush={gaPush}
      />
    );
  }

  return (
    <div className={cx(styles.container)} id="lfs">
      <a id="lf-top" />
      <Header
        headline={form.headline}
        modalType={modalType}
        activeStep={activeStep || 0}
        validSteps={validSteps}
        goToStep={goToStep}
      />
      <div className={cx(styles.content)} ref={contentRef}>
        {stepNewsletter && (
          <div className={cx(styles.stackColumn, styles.lightGrey)}>
            <div className={styles.lightGrey}>
              {form.subHeadline.replace(
                '[[model]]',
                currentModel.nameBodyText || currentModel.label
              )}
            </div>
            <h3
              aria-label="Tragen sie ihre Kontaktdaten ein"
              className={styles.headline3}
              tabIndex="-1"
            >
              Tragen sie ihre Kontaktdaten ein
            </h3>
            <div className={styles.clientType}>
              <div>
                <Radio
                  label="Privatkunde"
                  checked={clientType === 'Private'}
                  name="clientType"
                  value="Private"
                  onChange={() => setClientType('Private')}
                />
              </div>
              <div>
                <Radio
                  label="Gewerbekunde"
                  checked={clientType === 'Business'}
                  name="clientType"
                  value="Business"
                  onChange={() => setClientType('Business')}
                />
              </div>
            </div>
            <div className={styles.spaceBetweenStart}>
              <Input
                label="Vorname*"
                name="firstName"
                value={firstName}
                errorMessage={errors.firstName === 'init' ? '' : errors.firstName}
                onChange={setFirstName}
                extStyle={styles.root}
              />
              <Input
                label="Nachname*"
                name="lastName"
                value={lastName}
                errorMessage={errors.lastName === 'init' ? '' : errors.lastName}
                onChange={setLastName}
                extStyle={styles.root}
              />
            </div>
            <div className={styles.spaceBetweenStart}>
              <Input
                label="E-Mail-Adresse*"
                name="email"
                value={email}
                errorMessage={errors.email === 'init' ? '' : errors.email}
                onChange={setEmail}
                onFocus={() => {
                  if (errors.email === 'init') {
                    setErrors((current) => {
                      delete current.email;
                      return current;
                    });
                  }
                }}
                extStyle={styles.root}
              />
              <Input
                label="Rufnummer (optional)"
                name="telephone"
                value={telephone}
                errorMessage={errors.telephone === 'init' ? '' : errors.telephone}
                onChange={setTelephone}
                extStyle={styles.root}
              />
            </div>
            <AdvertisementCommunicationInfo
              dealer={dealer}
              emailOptIn={emailOptIn}
              errors={errors}
              setEmailOptIn={setEmailOptIn}
              setTelephoneOptIn={setTelephoneOptIn}
              telephoneOptIn={telephoneOptIn}
              resize={resize}
              optionsAvailable={false}
            />
            <br />
            <br />
            <br />
          </div>
        )}
        {step1visible && (
          <div className={styles.stackColumn}>
            <h3
              aria-label="Wählen Sie Ihr Fahrzeug"
              className={styles.headline3}
              tabIndex="-1"
              aria-describedby={[
                consumptionText ? 'leadform-consumption-text' : null,
                model ? 'leadform-footnotes' : null,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              Wählen Sie Ihr Fahrzeug
            </h3>
            <Dropdown
              name="model"
              label="Modell wählen*"
              value={model}
              onChange={handleModelChange}
              options={modelOptions}
            />
            {showLargeCarIn && <CarImage carIn={model} carOut={modelBefore} />}
            {showLargeCarOut && <CarImage carIn="" carOut={model} />}
            {consumptionText && (
              <div
                id="leadform-consumption-text"
                className={styles.lightGrey}
                dangerouslySetInnerHTML={{ __html: consumptionText }}
              />
            )}
            {model && (
              <div
                id="leadform-footnotes"
                className={cx(styles.lightGrey, !model ? styles.extraSpace : undefined)}
                dangerouslySetInnerHTML={{ __html: footnote }}
              />
            )}
            <br />
          </div>
        )}
        {step2visible && (
          <div className={cx(styles.stackColumn, styles.lightGrey)}>
            <div className={styles.lightGrey}>
              {form.subHeadline.replace(
                '[[model]]',
                currentModel.nameBodyText || currentModel.label
              )}
            </div>
            <h3
              aria-label="Tragen sie ihre Kontaktdaten ein"
              className={styles.headline3}
              tabIndex="-1"
            >
              Tragen sie ihre Kontaktdaten ein
            </h3>
            <div className={styles.spaceBetweenStart}>
              <Input
                label="Vorname*"
                name="firstName"
                value={firstName}
                errorMessage={errors.firstName === 'init' ? '' : errors.firstName}
                onChange={setFirstName}
                extStyle={styles.root}
              />
              <Input
                label="Nachname*"
                name="lastName"
                value={lastName}
                errorMessage={errors.lastName === 'init' ? '' : errors.lastName}
                onChange={setLastName}
                extStyle={styles.root}
              />
            </div>
            <div className={styles.spaceBetweenStart}>
              <Input
                label="E-Mail-Adresse*"
                name="email"
                value={email}
                errorMessage={errors.email === 'init' ? '' : errors.email}
                onChange={setEmail}
                onFocus={() => {
                  if (errors.email === 'init') {
                    setErrors((current) => {
                      delete current.email;
                      return current;
                    });
                  }
                }}
                extStyle={styles.root}
              />
              <Input
                label="Rufnummer (optional)"
                name="telephone"
                value={telephone}
                errorMessage={errors.telephone === 'init' ? '' : errors.telephone}
                onChange={setTelephone}
                extStyle={styles.root}
              />
            </div>
            {modalType.name === 'TEST_DRIVE' && (
              <div className={styles.textArea}>
                <Textarea
                  label={`Kommentar (optional)`}
                  placeholder={`z.B. bevorzugte Motorisierung / Getriebe`}
                  name="comment"
                  value={comment}
                  errorMessage={errors.comment}
                  onChange={setComment}
                  extStyle={styles.root}
                />
              </div>
            )}
            <AdvertisementCommunicationInfo
              dealer={dealer}
              emailOptIn={emailOptIn}
              errors={errors}
              setEmailOptIn={setEmailOptIn}
              setTelephoneOptIn={setTelephoneOptIn}
              telephoneOptIn={telephoneOptIn}
              resize={resize}
            />
            <br />
            <br />
            <br />
          </div>
        )}
        {step3visible && (
          <div className={styles.stackColumn}>
            {modalType.name === 'TEST_DRIVE' && (
              <div className={styles.stackColumn}>
                <div className={styles.lightGrey}>
                  Bitte beachten Sie, dass der Wunschtermin nur ein Vorschlag ist und wir das Datum
                  für die tatsächliche Probefahrt nicht garantieren können.
                </div>
                <h3
                  aria-label="Wunschtermin wählen (optional)"
                  className={styles.headline3}
                  tabIndex="-1"
                >
                  Wunschtermin wählen (optional)
                </h3>

                <div className={styles.desktopSideBySide}>
                  <InputDate
                    className={styles.dateContainer}
                    disabledDays={disabledDays} // list of provided bank-holidays that are excluded
                    disabledWeekdays={[0]} // Sundays are excluded
                    errorMessage="Sonn- und Feiertage sind nicht möglich"
                    pastMessage="Vergangene Tage sind nicht möglich"
                    label="Datum"
                    min={minDate}
                    name="appointmentDate"
                    onChange={setAppointmentDate}
                    setError={(appointmentDate) => {
                      setErrors(({ appointmentDate: _, ...rest }) =>
                        appointmentDate ? { ...rest, appointmentDate } : rest
                      );
                    }}
                    value={appointmentDate}
                  />
                  <div className={cx(styles.timeContainer)}>
                    <ul className={styles.stackColumnMobileLess}>
                      <li>
                        <Radio
                          label="Beliebig"
                          checked={!preferredTime}
                          name="preferredTime"
                          value=""
                          onChange={() => setPreferredTime(undefined)}
                        />
                      </li>
                      <li>
                        <Radio
                          label="Vormittags"
                          checked={preferredTime === 'morning'}
                          name="preferredTime"
                          value="morning"
                          onChange={() => setPreferredTime('morning')}
                        />
                      </li>
                      <li>
                        <Radio
                          label="Nachmittags"
                          checked={preferredTime === 'afternoon'}
                          name="preferredTime"
                          value="afternoon"
                          onChange={() => setPreferredTime('afternoon')}
                        />
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {modalType.name === 'REQUEST_FOR_PROPOSAL' && (
              <div className={styles.stackColumn}>
                <div className={styles.lightGrey}>
                  Gerne stellen wir Ihnen ein maßgeschneidertes Angebot zur Verfügung.
                </div>
                <h3
                  aria-label="Ihre Zusatzinformationen"
                  className={styles.headline3}
                  tabIndex="-1"
                >
                  Ihre Zusatzinformationen
                </h3>
                <ul className={styles.clientType}>
                  <li>
                    <Radio
                      label="Privatkunde"
                      checked={clientType === 'Private'}
                      name="clientType"
                      value="Private"
                      onChange={() => setClientType('Private')}
                    />
                  </li>
                  <li>
                    <Radio
                      label="Gewerbekunde"
                      checked={clientType === 'Business'}
                      name="clientType"
                      value="Business"
                      onChange={() => setClientType('Business')}
                    />
                  </li>
                </ul>
                <Dropdown
                  name="preferredPayment"
                  label="Bevorzugte Bezahlart (optional)"
                  value={preferredPayment}
                  onChange={setPreferredPayment}
                  options={preferredPaymentOptions}
                />
                <div className={styles.textArea}>
                  <Textarea
                    label={'Kommentar (optional)'}
                    placeholder={`z.B. bevorzugte Motorisierung / Getriebe, Angaben zu Ihrem maximalen Budget, einer bestimmten Ausstattungsvariante oder Farbe`}
                    name="comment"
                    value={comment}
                    errorMessage={errors.comment}
                    onChange={setComment}
                    extStyle={styles.root}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {step4visible && (
          <div className={styles.stackColumn}>
            <h3 aria-label="Übersicht Ihrer Angaben" className={styles.headline3} tabIndex="-1">
              Übersicht Ihrer Angaben
            </h3>
            <div className={styles.stackColumnZeroGap}>
              <div className={cx(styles.stackRowLess)}>
                <h3 aria-label="Modellauswahl" className={styles.headline3}>
                  Modellauswahl
                </h3>
                <Button
                  onClick={() => goToStep(0, 3, 'form field change')}
                  ariaLabel={'Modellauswahl ändern'}
                  label="Ändern"
                  variant="secondary"
                  small
                  additionalProps={{
                    style: { fontSize: '18px', gap: '8px', transform: 'scale(0.8)' },
                  }}
                  icon="pencil"
                />
              </div>
              {model !== '' && (
                <CarImage
                  carIn={lastStep !== keepStep ? model : ''}
                  carOut={lastStep === keepStep ? model : ''}
                  last
                />
              )}
            </div>

            <div className={styles.desktopSideBySide}>
              <div className={styles.stackColumnZeroGap}>
                <div className={cx(styles.stackRowLess)}>
                  <h3 aria-label="Kontaktdaten" className={styles.headline3}>
                    Kontaktdaten
                  </h3>
                  <Button
                    onClick={() => goToStep(1, 3, 'form field change')}
                    ariaLabel={'Kontaktdaten ändern'}
                    label="Ändern"
                    variant="secondary"
                    small
                    additionalProps={{
                      style: { fontSize: '18px', gap: '8px', transform: 'scale(0.8)' },
                    }}
                    icon="pencil"
                  />
                </div>
                <div className={cx(styles.infoContainer)}>
                  <div>
                    {firstName} {lastName}
                  </div>
                  <div>{email}</div>
                  <div>{telephone}</div>
                  {modalType.name === 'TEST_DRIVE' && (
                    <div>Ihr Kommentar: {comment || 'Keine Angabe'}</div>
                  )}
                </div>
              </div>
              {modalType.name === 'TEST_DRIVE' && (
                <div className={styles.stackColumnZeroGap}>
                  <div className={cx(styles.stackRowLess)}>
                    <h3 aria-label="Wunschtermin" className={styles.headline3}>
                      Wunschtermin
                    </h3>
                    <Button
                      onClick={() => goToStep(2, 3, 'form field change')}
                      ariaLabel={'Wunschtermin ändern'}
                      label="Ändern"
                      variant="secondary"
                      small
                      additionalProps={{
                        style: { fontSize: '18px', gap: '8px', transform: 'scale(0.8)' },
                      }}
                      icon="pencil"
                    />
                  </div>
                  <div className={cx(styles.infoContainer)}>
                    <div>Datum: {appointmentDateString}</div>
                    {preferredTime ? (
                      <div>
                        Tageszeit: {preferredTime === 'morning' ? 'Vormittags' : 'Nachmittags'}
                      </div>
                    ) : (
                      <div>Tageszeit: Beliebig</div>
                    )}
                  </div>
                </div>
              )}
              {modalType.name === 'REQUEST_FOR_PROPOSAL' && (
                <div className={styles.stackColumn} style={{ gap: '0' }}>
                  <div className={cx(styles.stackRowLess)}>
                    <h3 aria-label="Zusatzinfos" className={styles.headline3}>
                      Zusatzinfos
                    </h3>
                    <Button
                      onClick={() => goToStep(2, 3, 'form field change')}
                      label="Ändern"
                      variant="secondary"
                      small
                      additionalProps={{
                        style: { fontSize: '18px', gap: '8px', transform: 'scale(0.8)' },
                      }}
                      icon="pencil"
                    />
                  </div>
                  <div className={cx(styles.infoContainer)}>
                    <div>{clientType === 'Private' ? 'Privatkunde' : 'Gewerbekunde'}</div>
                    <div>
                      Ihre Zahlart:{' '}
                      {preferredPayment
                        ? preferredPaymentOptions.find((ppo) => ppo.value === preferredPayment)
                            .label
                        : 'Keine Angabe'}
                    </div>
                    {modalType.name === 'REQUEST_FOR_PROPOSAL' && (
                      <div>Ihr Kommentar: {comment || 'Keine Angabe'}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer
        model={model}
        goBack={goBack}
        goNext={goNext}
        loading={loading}
        hasShadow={hasShadow}
        transferData={transferData}
        showSmallCarIn={showSmallCarIn}
        showSmallCarOut={showSmallCarOut}
        activeStep={activeStep || 0}
        lastStep={lastStep}
        nextEnabled={validSteps[activeStep]}
      />
    </div>
  );
}
